import * as turf from '@turf/turf';
import type { Feature, Position } from 'geojson';
import { arcgisToGeoJSON } from '@terraformer/arcgis';
import * as projectOperator from '@arcgis/core/geometry/operators/projectOperator';
import { observability } from 'utils/observability';
import { SpatialReferenceWgs84 } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/spatialReference';
import {
  buildGeoLocationFromGeoJson,
  type GeoLocation,
} from './buildGeoLocationFromGeoJson';

export const useLocationFromArcgisGeometry = (
  reverseGeocode: (
    longitude: number,
    latitude: number
  ) => Promise<{ address?: string; placeName?: string }>
) => {
  return async (
    geometry: __esri.Geometry,
    fallbackName?: string
  ): Promise<GeoLocation | undefined> => {
    await projectOperator.load();
    const projected = projectOperator.execute(
      geometry as __esri.GeometryUnion,
      SpatialReferenceWgs84
    );
    if (!projected) {
      observability.captureException(
        new Error('Projection failed in GeoLocationComponent')
      );
      return undefined;
    }

    const result = Array.isArray(projected) ? projected[0] : projected;
    const geoJson = arcgisToGeoJSON(result.toJSON());

    if (geoJson.type !== 'Point' && geoJson.type !== 'Polygon') {
      return undefined;
    }

    let centroidFeature: Feature;
    let centerLongLat: [number, number];

    if (geoJson.type === 'Point') {
      const [longitude, latitude] = geoJson.coordinates as [number, number];
      centerLongLat = [longitude, latitude];
      centroidFeature = {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: centerLongLat },
        properties: {},
      };
    } else {
      const poly = turf.polygon(geoJson.coordinates as Position[][]);
      const centroid = turf.centroid(poly);
      centerLongLat = centroid.geometry.coordinates as [number, number];
      centroidFeature = centroid;
    }

    const { address, placeName } = await reverseGeocode(
      centerLongLat[0],
      centerLongLat[1]
    );

    return buildGeoLocationFromGeoJson({
      geometry: geoJson,
      centroid: centroidFeature,
      address: address || fallbackName,
      placeName: placeName || fallbackName,
    });
  };
};
